// modules
import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

// components
import { AgeInputComponent } from './components/form/age-input/age-input.component';
import { TabsComponent } from './tabs/tabs.component';
import { BackButtonComponent } from './back-button/back-button.component';
import { AppHeaderComponent } from './app-header/app-header.component';
import { AppFooterDesktopComponent } from './app-footer/app-footer-desktop/app-footer-desktop.component';
import { AppFooterTouchComponent } from './app-footer/app-footer-touch/app-footer-touch.component';
import { WeightInputComponent } from './components/form/weight-input/weight-input.component';
import { HeightInputComponent } from './components/form/height-input/height-input.component';
import { GenderSelectorComponent } from './components/form/gender-selector/gender-selector.component';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import { IngredientsComponent } from './ingredients/ingredients.component';
import { IngredientComponent } from './ingredient/ingredient.component';
import { MembersComponent } from './members/members.component';
import { MemberComponent } from './member/member.component';
import { NotificationComponent } from './notification/notification.component';
import { ExerciseSelectorComponent } from './components/form/exercise-selector/exercise-selector.component';
import { RecipeTagComponent } from './recipe-tag/recipe-tag.component';
import { CardInputComponent } from './card-input/card-input.component';
import { LangSelectorComponent } from './lang-selector/lang-selector.component';
import { DayTogglerComponent } from './components/form/day-toggler/day-toggler.component';
import { DaySelectorComponent } from './components/form/day-selector/day-selector.component';
import { CheckboxButtonComponent } from './components/form/checkbox-button/checkbox-button.component';
import { RadioButtonComponent } from './components/form/radio-button/radio-button.component';
import { AppLogoComponent } from './app-logo/app-logo.component';
import { BmiComponent } from './bmi/bmi.component';
import { CheckboxComponent } from './components/form/checkbox/checkbox.component';
import { FooterSocialComponent } from './footer-social/footer-social.component';
import { ContainerBackComponent } from './container-back/container-back.component';
import { PopoversBackComponent } from './app-popovers-back/app-popovers-back.component';
import { ContainerComponent } from './container/container.component';
import { CardComponent } from './card/card.component';
import { ButtonComponent } from './button/button.component';
import { ButtonGroupComponent } from './button-group/button-group.component';
import { MessageComponent } from './message/message.component';
import { OverlayComponent } from './overlay/overlay.component';

// services
import { AppFooterService } from './app-footer/app-footer.service';
import { AuthGuard } from './services/auth.guard';
import { AuthService } from './services/auth.service';
import { ProfileService } from './services/profile.service';
import { OnlineGuard } from './online-guard.service';
// directives
import { LimitNumberInputDirective } from './directives/limit-number-input.directive';
import {
  MinValidatorDirective,
  MaxValidatorDirective,
  LettersValidatorDirective,
  EmailValidatorDirective,
} from './validators/custom.validator';
import { CharLimitDirective } from './directives/char-limit.directive';
import { CreditCardDirective } from './directives/credit-card.directive';

// pipes
import { OrderByPipe } from './pipes/sort.pipe';
import { StripeAmountPipe } from './pipes/stripe-amount.pipe';
import { DaysStringPipe } from './pipes/days.pipe';
import { NumberSuffixPipe } from './pipes/number-suffix.pipe';
import { EpochPipe } from './pipes/epoch.pipe';
import { LongDatePipe } from './pipes/long-date.pipe';
import { ShortDatePipe } from './pipes/short-date.pipe';
import { SortMealsPipe } from './pipes/meals.pipe';
import { RoundAmountPipe } from './pipes/round.pipe';
import { EstDayPipe } from './pipes/est-day.pipe';
import { EstMonthPipe } from './pipes/est-month.pipe';
import { AisleIngredientPipe } from './pipes/aisle-ingredient.pipe';
import { PoundsPipe } from './pipes/pounds.pipe';
import { StonePipe } from './pipes/stone.pipe';
import { ToFixedPipe } from './pipes/to-fixed.pipe';
import { PriceSplitPipe } from './pipes/price-split.pipe';
import { LangSwitcherComponent } from './lang-switcher/lang-switcher.component';
import { SubscriptionStatusComponent } from './subscription-status/subscription-status.component';
import { ExclusionsComponent } from './components/form/exclusions/exclusions.component';
import { ExclusionsTogglerComponent } from './components/exclusions/exclusions-toggler/exclusions-toggler.component';
import { ExclusionsSummaryComponent } from './components/exclusions/exclusions-summary/exclusions-summary.component';
import { GenderComponent } from './components/form/gender/gender.component';
import { MaternityComponent } from './components/form/maternity/maternity.component';
import { MenuRecalculateComponent } from './components/menu-recalculate/menu-recalculate.component';
import { TagsComponent } from './components/tags/tags.component';
import { TagComponent } from './components/tag/tag.component';
import { PriceCategoryPipe } from './pipes/price-category.pipe';
import { IconComponent } from './components/icon/icon.component';
import { MenuAdjustComponent } from './components/menu-adjust/menu-adjust.component';
import { HelpTooltipComponent } from './help-tooltip/help-tooltip.component';

@NgModule({
  imports: [CommonModule, RouterModule, FormsModule, AlertModule.forRoot(), ModalModule.forRoot(), TooltipModule.forRoot()],
  declarations: [
    CharLimitDirective,
    LimitNumberInputDirective,
    CreditCardDirective,
    EpochPipe,
    LongDatePipe,
    ShortDatePipe,
    AisleIngredientPipe,
    OrderByPipe,
    EstDayPipe,
    EstMonthPipe,
    DaysStringPipe,
    StripeAmountPipe,
    NumberSuffixPipe,
    SortMealsPipe,
    RoundAmountPipe,
    TabsComponent,
    AppHeaderComponent,
    AppFooterDesktopComponent,
    AppFooterTouchComponent,
    BackButtonComponent,
    WeightInputComponent,
    HeightInputComponent,
    AgeInputComponent,
    IngredientsComponent,
    IngredientComponent,
    MembersComponent,
    MemberComponent,
    RecipeTagComponent,
    CardInputComponent,
    NotificationComponent,
    ExerciseSelectorComponent,
    GenderSelectorComponent,
    ProgressBarComponent,
    MinValidatorDirective,
    MaxValidatorDirective,
    LettersValidatorDirective,
    EmailValidatorDirective,
    LangSelectorComponent,
    DaySelectorComponent,
    DayTogglerComponent,
    CheckboxButtonComponent,
    RadioButtonComponent,
    AppLogoComponent,
    StonePipe,
    ToFixedPipe,
    PoundsPipe,
    BmiComponent,
    CheckboxComponent,
    PriceSplitPipe,
    FooterSocialComponent,
    ContainerComponent,
    ContainerBackComponent,
    PopoversBackComponent,
    CardComponent,
    ButtonComponent,
    ButtonGroupComponent,
    MessageComponent,
    LangSwitcherComponent,
    OverlayComponent,
    SubscriptionStatusComponent,
    ExclusionsComponent,
    ExclusionsTogglerComponent,
    ExclusionsSummaryComponent,
    GenderComponent,
    MaternityComponent,
    MenuRecalculateComponent,
    TagsComponent,
    TagComponent,
    PriceCategoryPipe,
    IconComponent,
    MenuAdjustComponent,
    HelpTooltipComponent,
  ],
  exports: [
    CharLimitDirective,
    LimitNumberInputDirective,
    CreditCardDirective,
    EpochPipe,
    LongDatePipe,
    ShortDatePipe,
    EstDayPipe,
    EstMonthPipe,
    AisleIngredientPipe,
    OrderByPipe,
    DaysStringPipe,
    NumberSuffixPipe,
    SortMealsPipe,
    StripeAmountPipe,
    RoundAmountPipe,
    TabsComponent,
    AppHeaderComponent,
    AppFooterDesktopComponent,
    AppFooterTouchComponent,
    BackButtonComponent,
    WeightInputComponent,
    HeightInputComponent,
    AgeInputComponent,
    IngredientsComponent,
    IngredientComponent,
    MembersComponent,
    MemberComponent,
    RecipeTagComponent,
    CardInputComponent,
    NotificationComponent,
    ExerciseSelectorComponent,
    GenderSelectorComponent,
    ProgressBarComponent,
    LangSelectorComponent,
    DaySelectorComponent,
    DayTogglerComponent,
    CheckboxButtonComponent,
    RadioButtonComponent,
    AppLogoComponent,
    StonePipe,
    ToFixedPipe,
    PoundsPipe,
    BmiComponent,
    CheckboxComponent,
    PriceSplitPipe,
    FooterSocialComponent,
    ContainerComponent,
    ContainerBackComponent,
    PopoversBackComponent,
    CardComponent,
    ButtonComponent,
    ButtonGroupComponent,
    MessageComponent,
    LangSwitcherComponent,
    OverlayComponent,
    SubscriptionStatusComponent,
    ExclusionsComponent,
    ExclusionsTogglerComponent,
    ExclusionsSummaryComponent,
    GenderComponent,
    MaternityComponent,
    MenuRecalculateComponent,
    TagsComponent,
    TagComponent,
    PriceCategoryPipe,
    IconComponent,
    MenuAdjustComponent,
    HelpTooltipComponent,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [AppFooterService, AuthService, ProfileService, AuthGuard, OnlineGuard],
    };
  }
}
